/**
 * エリアグループラベル1枚のマーカー
 *
 * DOMの生成・件数表示の更新・クリックの通知だけを持つ
 * どのラベルをいつ出すかは AreaLabelManager が決める
 */

import type { AreaMembership } from '@/points/arealabels/AreaMembership.ts'

/**
 * ラベルの各要素に付けるクラス
 *
 * 組織のscssから見た目を上書きできるよう、名称と件数にも個別に付ける
 * 他のマーカーのスタイルと衝突しないよう、いずれも area-group-label で始める
 */
const LABEL_CLASS = {
  /**
   * ラベル全体を包む要素
   */
  ROOT: 'area-group-label',

  /**
   * 都道府県名・国名を出す要素
   */
  NAME: 'area-group-label-name',

  /**
   * 件数を出す要素
   */
  COUNT: 'area-group-label-count',

  /**
   * 消えるアニメーションの間だけ付く
   *
   * 地図から外すのを待つ間、透明になった状態を保つ
   */
  HIDING: 'is-hiding',
} as const

/**
 * 消えるアニメーションの長さ（ミリ秒）
 *
 * style.scss の area-group-label-out の長さと合わせる
 * これより短いと消え切る前に地図から外れ、長いと消えたラベルが地図に残る
 */
const HIDE_DURATION_MS = 180

/**
 * ラベルの重なり順の下限
 *
 * 現在地マーカーはzIndexを持たず緯度で決まり、日本国内なら (90 - 緯度) * 1000000 の目安で 44000000〜66000000 に収まる
 * その帯を越える値にして、ラベルを常に現在地より前に出す
 *
 * InfoWindow（200000000）より後ろに置き、詳細を開いたときは隠れるようにする
 */
const Z_INDEX_BASE = 100000000

/**
 * 緯度1度あたりの重なり順の差
 *
 * zIndexを指定しないマーカーは南ほど前に出るので、ラベルもその順に揃える
 * 緯度は -90〜90 なので下限への加算は 180000 までに収まり、InfoWindow（200000000）の帯には届かない
 */
const Z_INDEX_PER_LATITUDE = 1000

/**
 * ホバー中のラベルの重なり順
 *
 * 緯度で決まる値は下限 + 180000 までに収まるので、その帯を越える値にして重なった相手より必ず前に出す
 */
const Z_INDEX_HOVERED = Z_INDEX_BASE + 1000000

export class AreaLabelMarker {
  /**
   * このラベルが表す割り当て
   *
   * クリックの通知に添えるので、呼び出し側はマーカーと割り当てを別に持たなくてよい
   */
  readonly membership: AreaMembership

  private _map: google.maps.Map
  private _marker: google.maps.marker.AdvancedMarkerElement
  private _root: HTMLElement
  private _countElement: HTMLElement

  /**
   * 緯度から決まる重なり順
   *
   * ホバーで前に出したあと戻すので、元の値を持つ
   */
  private _zIndex: number

  /**
   * 消えるアニメーションが終わってから地図から外すためのタイマー
   *
   * 消えている途中で表示へ戻ることがあるので、切り替えのたびに取り消せるよう持つ
   */
  private _hideTimer: number | null = null

  /**
   * ラベルを地図に出しているか
   *
   * 消えるアニメーションの途中はまだ地図に残っているが、falseになる
   * setVisible は地図の移動ごとに呼ばれるので、変わらないときは何もしない
   */
  private _isVisible = false

  /**
   * @param map 表示先の地図、setVisible で付け外しする
   * @param membership このラベルが表す割り当て、座標はエリアから読む
   * @param options label はラベルに表示する名称、言語に応じてどれを表示するかは呼び出し側が決める
   *   onClicked はラベルがクリックされたときに割り当てを引数に渡して呼ぶ
   *   クリック後の地図操作は呼び出し側が持つ
   */
  constructor(map: google.maps.Map, membership: AreaMembership, options: {
    label: string
    onClicked: (membership: AreaMembership) => void
  }) {
    this._map = map
    this.membership = membership

    const area = membership.area
    const elements = _createElements(options.label)

    this._root = elements.root
    this._countElement = elements.count
    this._zIndex = _zIndexFor(area.location.latitude)

    this._marker = new google.maps.marker.AdvancedMarkerElement({
      // 件数が確定してから出すので、生成時は地図に載せない
      map: null,
      content: this._root,
      position: new google.maps.LatLng(area.location.latitude, area.location.longitude),
      zIndex: this._zIndex,
      gmpClickable: true,
    })

    // 重なったラベルは名称と件数が隠れるので、触れている間だけ前に出して読めるようにする
    this._root.addEventListener('mouseenter', () => {
      // 消えている途中のラベルは地図に残っているので、前に出さない
      if (!this._isVisible) {
        return
      }

      this._marker.zIndex = Z_INDEX_HOVERED
    })

    this._root.addEventListener('mouseleave', () => {
      this._marker.zIndex = this._zIndex
    })

    this._marker.addListener('click', () => {
      // 消えている途中のラベルは地図に残っているので、押せないようにする
      if (!this._isVisible) {
        return
      }

      options.onClicked(this.membership)
    })
  }

  /**
   * 件数の表示を更新する
   */
  setCount(count: number) {
    this._countElement.textContent = String(count)
  }

  /**
   * 地図への表示を切り替える
   *
   * 出すときは地図に載せるだけで、出るアニメーションは載った時点で再生される
   * 消すときはアニメーションを見せてから地図から外す
   */
  setVisible(visible: boolean) {
    if (visible === this._isVisible) {
      return
    }

    this._isVisible = visible
    this._clearHideTimer()

    if (visible) {
      this._attach()
      return
    }

    this._detachAfterAnimation()
  }

  /**
   * 地図から取り除く
   *
   * setVisible(false) と違い、このマーカーを使わなくなったときに呼ぶ
   * アニメーションを待たずに外す
   */
  remove() {
    this._clearHideTimer()
    this._isVisible = false
    this._detach()
  }

  /**
   * 地図から外すのを待つタイマーを取り消す
   */
  private _clearHideTimer() {
    if (this._hideTimer === null) {
      return
    }

    clearTimeout(this._hideTimer)
    this._hideTimer = null
  }

  /**
   * 地図に載せる
   *
   * 出るアニメーションは載った時点で再生されるので、ここでは開始しない
   */
  private _attach() {
    this._root.classList.remove(LABEL_CLASS.HIDING)
    this._marker.map = this._map
  }

  /**
   * 消えるアニメーションを見せてから地図から外す
   *
   * 外すまでの間もラベルは地図に残るので、透明を保つクラスを付ける
   */
  private _detachAfterAnimation() {
    this._root.classList.add(LABEL_CLASS.HIDING)

    this._hideTimer = window.setTimeout(() => {
      this._hideTimer = null
      this._detach()
    }, HIDE_DURATION_MS)
  }

  /**
   * 地図から外して、消えかけの見た目も戻す
   */
  private _detach() {
    this._root.classList.remove(LABEL_CLASS.HIDING)
    this._marker.zIndex = this._zIndex
    this._marker.map = null
  }
}

/**
 * 緯度から重なり順を決める
 *
 * @param latitude ラベルを出す位置の緯度
 */
function _zIndexFor(latitude: number): number {
  return Z_INDEX_BASE + Math.round((90 - latitude) * Z_INDEX_PER_LATITUDE)
}

/**
 * ラベルのDOMを組む
 *
 * 件数は生成時には決まらないので、更新できるよう要素を返す
 *
 * @param label ラベルに出す名称
 * @returns root は全体を包む要素、count は件数を出す要素
 */
function _createElements(label: string): { root: HTMLElement, count: HTMLElement } {
  const root = document.createElement('div')
  root.className = LABEL_CLASS.ROOT

  const nameElement = document.createElement('span')
  nameElement.className = LABEL_CLASS.NAME
  nameElement.textContent = label
  root.appendChild(nameElement)

  const countElement = document.createElement('span')
  countElement.className = LABEL_CLASS.COUNT
  root.appendChild(countElement)

  return { root: root, count: countElement }
}
